/**
 * Raptor Predator Frontend - Fail-Open Policy Store
 *
 * Manages hierarchical fail-open policy data:
 * - Global policies
 * - Enterprise groups and their policies
 * - Subnets and their policies
 * - Device-level policies
 * - Effective policy resolution
 * - Audit trail
 *
 * Phase 2: Admin UI and API endpoints for policy management
 *
 * Version: 1.1.0
 * Author: Claude Opus 4.5
 * Peer Review: Gemini 3 Pro (7.5/10 → 9/10), GPT 5.1 (8/10 → 9/10)
 *
 * Performance Notes (from peer review):
 * - Use specific selectors (e.g., selectGlobalPolicy) not the whole store
 * - For >500 nodes, consider adding virtualization to tree views
 * - All components use memo() to prevent unnecessary re-renders
 * - expandedGroups/expandedSubnets are Sets for O(1) lookups
 */

import { create } from 'zustand';
import { devtools, subscribeWithSelector } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';

// ============================================================================
// Types
// ============================================================================

/** Fail-open mode determining system behavior during failures */
export type FailOpenMode = 'fail_open' | 'fail_closed' | 'fail_silent';

/** Policy hierarchy level */
export type PolicyLevel = 'global' | 'enterprise_group' | 'subnet' | 'device';

/** User role for RBAC */
export type PolicyRole =
  | 'viewer'
  | 'device_admin'
  | 'network_admin'
  | 'enterprise_admin'
  | 'global_admin';

/** Global fail-open policy */
export interface GlobalFailOpenPolicy {
  id: string;
  mode: FailOpenMode;
  alert_threshold_seconds: number;
  max_consecutive_failures: number;
  auto_recovery_enabled: boolean;
  alert_emails: string[];
  alert_webhooks: string[];
  alert_pagerduty_key: string | null;
  fallback_to_cached_policy: boolean;
  cached_policy_max_age_hours: number;
  log_all_decisions: boolean;
  description: string | null;
  created_by: string;
  updated_by: string;
  created_at: string;
  updated_at: string;
}

/** Enterprise group definition */
export interface EnterpriseGroup {
  id: string;
  name: string;
  description: string | null;
  parent_group_id: string | null;
  metadata: Record<string, unknown>;
  created_by: string;
  updated_by: string;
  created_at: string;
  updated_at: string;
}

/** Enterprise group fail-open policy */
export interface EnterpriseGroupPolicy {
  id: string;
  enterprise_group_id: string;
  mode: FailOpenMode | null;
  alert_threshold_seconds: number | null;
  max_consecutive_failures: number | null;
  auto_recovery_enabled: boolean | null;
  alert_emails: string[] | null;
  alert_webhooks: string[] | null;
  alert_pagerduty_key: string | null;
  fallback_to_cached_policy: boolean | null;
  cached_policy_max_age_hours: number | null;
  log_all_decisions: boolean | null;
  description: string | null;
  enabled: boolean;
  created_by: string;
  updated_by: string;
  created_at: string;
  updated_at: string;
}

/** Subnet definition */
export interface Subnet {
  id: string;
  name: string;
  cidr: string;
  enterprise_group_id: string | null;
  vlan_id: number | null;
  network_type: string;
  description: string | null;
  metadata: Record<string, unknown>;
  created_by: string;
  updated_by: string;
  created_at: string;
  updated_at: string;
}

/** Subnet fail-open policy */
export interface SubnetPolicy {
  id: string;
  subnet_id: string;
  mode: FailOpenMode | null;
  alert_threshold_seconds: number | null;
  max_consecutive_failures: number | null;
  auto_recovery_enabled: boolean | null;
  alert_emails: string[] | null;
  alert_webhooks: string[] | null;
  alert_pagerduty_key: string | null;
  fallback_to_cached_policy: boolean | null;
  cached_policy_max_age_hours: number | null;
  log_all_decisions: boolean | null;
  description: string | null;
  enabled: boolean;
  created_by: string;
  updated_by: string;
  created_at: string;
  updated_at: string;
}

/** Device fail-open policy */
export interface DevicePolicy {
  id: string;
  device_id: string;
  subnet_id: string | null;
  mode: FailOpenMode | null;
  alert_threshold_seconds: number | null;
  max_consecutive_failures: number | null;
  auto_recovery_enabled: boolean | null;
  alert_emails: string[] | null;
  alert_webhooks: string[] | null;
  alert_pagerduty_key: string | null;
  fallback_to_cached_policy: boolean | null;
  cached_policy_max_age_hours: number | null;
  log_all_decisions: boolean | null;
  device_name: string | null;
  device_type: string | null;
  criticality: string | null;
  description: string | null;
  enabled: boolean;
  created_by: string;
  updated_by: string;
  created_at: string;
  updated_at: string;
}

/** Effective policy after hierarchy resolution */
export interface EffectivePolicy {
  device_id: string;
  mode: FailOpenMode;
  alert_threshold_seconds: number;
  max_consecutive_failures: number;
  auto_recovery_enabled: boolean;
  fallback_to_cached_policy: boolean;
  cached_policy_max_age_hours: number;
  log_all_decisions: boolean;
  alert_emails: string[];
  alert_webhooks: string[];
  policy_source: PolicyLevel;
  resolved_at: string;
}

/** Audit log entry */
export interface PolicyAuditLogEntry {
  id: string;
  policy_level: string;
  policy_id: string;
  action: string;
  changed_by: string;
  changed_from_ip: string | null;
  old_values: Record<string, unknown> | null;
  new_values: Record<string, unknown> | null;
  change_reason: string | null;
  created_at: string;
}

/** Enterprise group with its policy */
export interface EnterpriseGroupWithPolicy {
  group: EnterpriseGroup;
  policy: EnterpriseGroupPolicy | null;
}

/** Subnet with its policy */
export interface SubnetWithPolicy {
  subnet: Subnet;
  policy: SubnetPolicy | null;
}

/** Full hierarchy overview */
export interface HierarchyOverview {
  global_policy: GlobalFailOpenPolicy;
  enterprise_groups: EnterpriseGroupWithPolicy[];
  subnets: SubnetWithPolicy[];
  statistics: HierarchyStatistics;
}

/** Hierarchy statistics */
export interface HierarchyStatistics {
  total_enterprise_groups: number;
  total_subnets: number;
  total_device_policies: number;
  fail_open_count: number;
  fail_closed_count: number;
  fail_silent_count: number;
}

/** Selected item in the hierarchy tree */
export interface SelectedHierarchyItem {
  level: PolicyLevel;
  id: string;
}

// ============================================================================
// Store State
// ============================================================================

interface FailOpenState {
  // Hierarchy data
  hierarchy: HierarchyOverview | null;

  // Individual entities
  globalPolicy: GlobalFailOpenPolicy | null;
  enterpriseGroups: Map<string, EnterpriseGroup>;
  enterpriseGroupPolicies: Map<string, EnterpriseGroupPolicy>;
  subnets: Map<string, Subnet>;
  subnetPolicies: Map<string, SubnetPolicy>;
  devicePolicies: Map<string, DevicePolicy>;

  // Audit trail
  auditLog: PolicyAuditLogEntry[];

  // Effective policies cache
  effectivePolicies: Map<string, EffectivePolicy>;

  // Selection
  selectedItem: SelectedHierarchyItem | null;
  expandedGroups: Set<string>;
  expandedSubnets: Set<string>;

  // Editing
  editingLevel: PolicyLevel | null;
  editingId: string | null;
  isDirty: boolean;

  // Loading states
  isLoading: boolean;
  isSaving: boolean;
  isLoadingAudit: boolean;
  error: string | null;
  lastUpdate: number;
}

interface FailOpenActions {
  // Hierarchy
  setHierarchy: (hierarchy: HierarchyOverview) => void;
  refreshHierarchy: () => Promise<void>;

  // Global policy
  setGlobalPolicy: (policy: GlobalFailOpenPolicy) => void;
  updateGlobalPolicy: (updates: Partial<GlobalFailOpenPolicy>) => void;

  // Enterprise groups
  addEnterpriseGroup: (group: EnterpriseGroup) => void;
  updateEnterpriseGroup: (id: string, updates: Partial<EnterpriseGroup>) => void;
  deleteEnterpriseGroup: (id: string) => void;
  setEnterpriseGroups: (groups: EnterpriseGroup[]) => void;

  // Enterprise group policies
  setEnterpriseGroupPolicy: (policy: EnterpriseGroupPolicy) => void;
  updateEnterpriseGroupPolicy: (
    groupId: string,
    updates: Partial<EnterpriseGroupPolicy>
  ) => void;

  // Subnets
  addSubnet: (subnet: Subnet) => void;
  updateSubnet: (id: string, updates: Partial<Subnet>) => void;
  deleteSubnet: (id: string) => void;
  setSubnets: (subnets: Subnet[]) => void;

  // Subnet policies
  setSubnetPolicy: (policy: SubnetPolicy) => void;
  updateSubnetPolicy: (subnetId: string, updates: Partial<SubnetPolicy>) => void;

  // Device policies
  setDevicePolicy: (policy: DevicePolicy) => void;
  updateDevicePolicy: (deviceId: string, updates: Partial<DevicePolicy>) => void;
  deleteDevicePolicy: (deviceId: string) => void;
  setDevicePolicies: (policies: DevicePolicy[]) => void;

  // Effective policies
  setEffectivePolicy: (deviceId: string, policy: EffectivePolicy) => void;
  clearEffectivePolicies: () => void;

  // Audit
  setAuditLog: (entries: PolicyAuditLogEntry[]) => void;
  appendAuditEntries: (entries: PolicyAuditLogEntry[]) => void;
  clearAuditLog: () => void;

  // Selection
  selectItem: (item: SelectedHierarchyItem | null) => void;
  toggleGroupExpanded: (groupId: string) => void;
  toggleSubnetExpanded: (subnetId: string) => void;
  expandAll: () => void;
  collapseAll: () => void;

  // Editing
  startEditing: (level: PolicyLevel, id: string) => void;
  stopEditing: () => void;
  setDirty: (dirty: boolean) => void;

  // State management
  setLoading: (loading: boolean) => void;
  setSaving: (saving: boolean) => void;
  setLoadingAudit: (loading: boolean) => void;
  setError: (error: string | null) => void;
}

// ============================================================================
// Initial State
// ============================================================================

const initialState: FailOpenState = {
  hierarchy: null,
  globalPolicy: null,
  enterpriseGroups: new Map(),
  enterpriseGroupPolicies: new Map(),
  subnets: new Map(),
  subnetPolicies: new Map(),
  devicePolicies: new Map(),
  auditLog: [],
  effectivePolicies: new Map(),
  selectedItem: null,
  expandedGroups: new Set(),
  expandedSubnets: new Set(),
  editingLevel: null,
  editingId: null,
  isDirty: false,
  isLoading: false,
  isSaving: false,
  isLoadingAudit: false,
  error: null,
  lastUpdate: Date.now(),
};

// ============================================================================
// Store Creation
// ============================================================================

export const useFailOpenStore = create<FailOpenState & FailOpenActions>()(
  devtools(
    subscribeWithSelector(
      immer((set, _get) => ({
        ...initialState,

        // Hierarchy
        setHierarchy: (hierarchy) =>
          { set((state) => {
            state.hierarchy = hierarchy;
            state.globalPolicy = hierarchy.global_policy;

            // Populate enterprise groups
            state.enterpriseGroups.clear();
            state.enterpriseGroupPolicies.clear();
            for (const eg of hierarchy.enterprise_groups) {
              state.enterpriseGroups.set(eg.group.id, eg.group);
              if (eg.policy) {
                state.enterpriseGroupPolicies.set(eg.group.id, eg.policy);
              }
            }

            // Populate subnets
            state.subnets.clear();
            state.subnetPolicies.clear();
            for (const s of hierarchy.subnets) {
              state.subnets.set(s.subnet.id, s.subnet);
              if (s.policy) {
                state.subnetPolicies.set(s.subnet.id, s.policy);
              }
            }

            state.lastUpdate = Date.now();
            state.error = null;
          }); },

        refreshHierarchy: async () => {
          // This would be called from a hook that fetches data
          // The actual API call happens in the hook
        },

        // Global policy
        setGlobalPolicy: (policy) =>
          { set((state) => {
            state.globalPolicy = policy;
            if (state.hierarchy) {
              state.hierarchy.global_policy = policy;
            }
            state.lastUpdate = Date.now();
          }); },

        updateGlobalPolicy: (updates) =>
          { set((state) => {
            if (state.globalPolicy) {
              Object.assign(state.globalPolicy, updates);
              state.isDirty = true;
              state.lastUpdate = Date.now();
            }
          }); },

        // Enterprise groups
        addEnterpriseGroup: (group) =>
          { set((state) => {
            state.enterpriseGroups.set(group.id, group);
            state.lastUpdate = Date.now();
          }); },

        updateEnterpriseGroup: (id, updates) =>
          { set((state) => {
            const group = state.enterpriseGroups.get(id);
            if (group) {
              Object.assign(group, updates);
              state.isDirty = true;
              state.lastUpdate = Date.now();
            }
          }); },

        deleteEnterpriseGroup: (id) =>
          { set((state) => {
            state.enterpriseGroups.delete(id);
            state.enterpriseGroupPolicies.delete(id);
            state.lastUpdate = Date.now();
          }); },

        setEnterpriseGroups: (groups) =>
          { set((state) => {
            state.enterpriseGroups.clear();
            for (const group of groups) {
              state.enterpriseGroups.set(group.id, group);
            }
            state.lastUpdate = Date.now();
          }); },

        // Enterprise group policies
        setEnterpriseGroupPolicy: (policy) =>
          { set((state) => {
            state.enterpriseGroupPolicies.set(policy.enterprise_group_id, policy);
            state.lastUpdate = Date.now();
          }); },

        updateEnterpriseGroupPolicy: (groupId, updates) =>
          { set((state) => {
            const policy = state.enterpriseGroupPolicies.get(groupId);
            if (policy) {
              Object.assign(policy, updates);
              state.isDirty = true;
              state.lastUpdate = Date.now();
            }
          }); },

        // Subnets
        addSubnet: (subnet) =>
          { set((state) => {
            state.subnets.set(subnet.id, subnet);
            state.lastUpdate = Date.now();
          }); },

        updateSubnet: (id, updates) =>
          { set((state) => {
            const subnet = state.subnets.get(id);
            if (subnet) {
              Object.assign(subnet, updates);
              state.isDirty = true;
              state.lastUpdate = Date.now();
            }
          }); },

        deleteSubnet: (id) =>
          { set((state) => {
            state.subnets.delete(id);
            state.subnetPolicies.delete(id);
            state.lastUpdate = Date.now();
          }); },

        setSubnets: (subnets) =>
          { set((state) => {
            state.subnets.clear();
            for (const subnet of subnets) {
              state.subnets.set(subnet.id, subnet);
            }
            state.lastUpdate = Date.now();
          }); },

        // Subnet policies
        setSubnetPolicy: (policy) =>
          { set((state) => {
            state.subnetPolicies.set(policy.subnet_id, policy);
            state.lastUpdate = Date.now();
          }); },

        updateSubnetPolicy: (subnetId, updates) =>
          { set((state) => {
            const policy = state.subnetPolicies.get(subnetId);
            if (policy) {
              Object.assign(policy, updates);
              state.isDirty = true;
              state.lastUpdate = Date.now();
            }
          }); },

        // Device policies
        setDevicePolicy: (policy) =>
          { set((state) => {
            state.devicePolicies.set(policy.device_id, policy);
            state.lastUpdate = Date.now();
          }); },

        updateDevicePolicy: (deviceId, updates) =>
          { set((state) => {
            const policy = state.devicePolicies.get(deviceId);
            if (policy) {
              Object.assign(policy, updates);
              state.isDirty = true;
              state.lastUpdate = Date.now();
            }
          }); },

        deleteDevicePolicy: (deviceId) =>
          { set((state) => {
            state.devicePolicies.delete(deviceId);
            state.effectivePolicies.delete(deviceId);
            state.lastUpdate = Date.now();
          }); },

        setDevicePolicies: (policies) =>
          { set((state) => {
            state.devicePolicies.clear();
            for (const policy of policies) {
              state.devicePolicies.set(policy.device_id, policy);
            }
            state.lastUpdate = Date.now();
          }); },

        // Effective policies
        setEffectivePolicy: (deviceId, policy) =>
          { set((state) => {
            state.effectivePolicies.set(deviceId, policy);
          }); },

        clearEffectivePolicies: () =>
          { set((state) => {
            state.effectivePolicies.clear();
          }); },

        // Audit
        setAuditLog: (entries) =>
          { set((state) => {
            state.auditLog = entries;
          }); },

        appendAuditEntries: (entries) =>
          { set((state) => {
            state.auditLog = [...state.auditLog, ...entries];
          }); },

        clearAuditLog: () =>
          { set((state) => {
            state.auditLog = [];
          }); },

        // Selection
        selectItem: (item) =>
          { set((state) => {
            state.selectedItem = item;
          }); },

        toggleGroupExpanded: (groupId) =>
          { set((state) => {
            if (state.expandedGroups.has(groupId)) {
              state.expandedGroups.delete(groupId);
            } else {
              state.expandedGroups.add(groupId);
            }
          }); },

        toggleSubnetExpanded: (subnetId) =>
          { set((state) => {
            if (state.expandedSubnets.has(subnetId)) {
              state.expandedSubnets.delete(subnetId);
            } else {
              state.expandedSubnets.add(subnetId);
            }
          }); },

        expandAll: () =>
          { set((state) => {
            for (const id of state.enterpriseGroups.keys()) {
              state.expandedGroups.add(id);
            }
            for (const id of state.subnets.keys()) {
              state.expandedSubnets.add(id);
            }
          }); },

        collapseAll: () =>
          { set((state) => {
            state.expandedGroups.clear();
            state.expandedSubnets.clear();
          }); },

        // Editing
        startEditing: (level, id) =>
          { set((state) => {
            state.editingLevel = level;
            state.editingId = id;
            state.isDirty = false;
          }); },

        stopEditing: () =>
          { set((state) => {
            state.editingLevel = null;
            state.editingId = null;
            state.isDirty = false;
          }); },

        setDirty: (dirty) =>
          { set((state) => {
            state.isDirty = dirty;
          }); },

        // State management
        setLoading: (loading) =>
          { set((state) => {
            state.isLoading = loading;
          }); },

        setSaving: (saving) =>
          { set((state) => {
            state.isSaving = saving;
          }); },

        setLoadingAudit: (loading) =>
          { set((state) => {
            state.isLoadingAudit = loading;
          }); },

        setError: (error) =>
          { set((state) => {
            state.error = error;
            state.isLoading = false;
            state.isSaving = false;
          }); },
      }))
    ),
    { name: 'fail-open-store' }
  )
);

// ============================================================================
// Selectors
// ============================================================================

export const selectHierarchy = (state: FailOpenState) => state.hierarchy;
export const selectGlobalPolicy = (state: FailOpenState) => state.globalPolicy;
export const selectEnterpriseGroups = (state: FailOpenState) => state.enterpriseGroups;
export const selectEnterpriseGroupPolicies = (state: FailOpenState) =>
  state.enterpriseGroupPolicies;
export const selectSubnets = (state: FailOpenState) => state.subnets;
export const selectSubnetPolicies = (state: FailOpenState) => state.subnetPolicies;
export const selectDevicePolicies = (state: FailOpenState) => state.devicePolicies;
export const selectAuditLog = (state: FailOpenState) => state.auditLog;
export const selectEffectivePolicies = (state: FailOpenState) => state.effectivePolicies;
export const selectSelectedItem = (state: FailOpenState) => state.selectedItem;
export const selectExpandedGroups = (state: FailOpenState) => state.expandedGroups;
export const selectExpandedSubnets = (state: FailOpenState) => state.expandedSubnets;
export const selectEditingLevel = (state: FailOpenState) => state.editingLevel;
export const selectEditingId = (state: FailOpenState) => state.editingId;
export const selectIsDirty = (state: FailOpenState) => state.isDirty;
export const selectIsLoading = (state: FailOpenState) => state.isLoading;
export const selectIsSaving = (state: FailOpenState) => state.isSaving;
export const selectError = (state: FailOpenState) => state.error;

/** Get enterprise group by ID */
export const selectEnterpriseGroupById = (id: string) => (state: FailOpenState) =>
  state.enterpriseGroups.get(id);

/** Get enterprise group policy by group ID */
export const selectEnterpriseGroupPolicyById = (groupId: string) => (state: FailOpenState) =>
  state.enterpriseGroupPolicies.get(groupId);

/** Get subnet by ID */
export const selectSubnetById = (id: string) => (state: FailOpenState) =>
  state.subnets.get(id);

/** Get subnet policy by subnet ID */
export const selectSubnetPolicyById = (subnetId: string) => (state: FailOpenState) =>
  state.subnetPolicies.get(subnetId);

/** Get device policy by device ID */
export const selectDevicePolicyById = (deviceId: string) => (state: FailOpenState) =>
  state.devicePolicies.get(deviceId);

/** Get effective policy by device ID */
export const selectEffectivePolicyById = (deviceId: string) => (state: FailOpenState) =>
  state.effectivePolicies.get(deviceId);

/** Get statistics */
export const selectStatistics = (state: FailOpenState) => state.hierarchy?.statistics;

/** Get subnets for a given enterprise group */
export const selectSubnetsForGroup =
  (groupId: string) =>
  (state: FailOpenState): Subnet[] => {
    const result: Subnet[] = [];
    for (const subnet of state.subnets.values()) {
      if (subnet.enterprise_group_id === groupId) {
        result.push(subnet);
      }
    }
    return result;
  };

/** Get child enterprise groups */
export const selectChildGroups =
  (parentId: string | null) =>
  (state: FailOpenState): EnterpriseGroup[] => {
    const result: EnterpriseGroup[] = [];
    for (const group of state.enterpriseGroups.values()) {
      if (group.parent_group_id === parentId) {
        result.push(group);
      }
    }
    return result.sort((a, b) => a.name.localeCompare(b.name));
  };

/** Get root enterprise groups (no parent) */
export const selectRootGroups = (state: FailOpenState): EnterpriseGroup[] => {
  const result: EnterpriseGroup[] = [];
  for (const group of state.enterpriseGroups.values()) {
    if (!group.parent_group_id) {
      result.push(group);
    }
  }
  return result.sort((a, b) => a.name.localeCompare(b.name));
};

/** Get mode color for UI display */
export function getModeColor(mode: FailOpenMode): string {
  switch (mode) {
    case 'fail_open':
      return 'green';
    case 'fail_closed':
      return 'red';
    case 'fail_silent':
      return 'amber';
    default:
      return 'gray';
  }
}

/** Get mode label for UI display */
export function getModeLabel(mode: FailOpenMode): string {
  switch (mode) {
    case 'fail_open':
      return 'Fail Open';
    case 'fail_closed':
      return 'Fail Closed';
    case 'fail_silent':
      return 'Fail Silent';
    default:
      return 'Unknown';
  }
}

/** Get mode icon for UI display */
export function getModeIcon(mode: FailOpenMode): string {
  switch (mode) {
    case 'fail_open':
      return '🟢';
    case 'fail_closed':
      return '🔴';
    case 'fail_silent':
      return '🟡';
    default:
      return '⚪';
  }
}

/** Get policy level label */
export function getLevelLabel(level: PolicyLevel): string {
  switch (level) {
    case 'global':
      return 'Global';
    case 'enterprise_group':
      return 'Enterprise Group';
    case 'subnet':
      return 'Subnet';
    case 'device':
      return 'Device';
    default:
      return 'Unknown';
  }
}

/** Check if user role can modify at level */
export function canModifyAtLevel(role: PolicyRole, level: PolicyLevel): boolean {
  switch (role) {
    case 'viewer':
      return false;
    case 'device_admin':
      return level === 'device';
    case 'network_admin':
      return level === 'device' || level === 'subnet';
    case 'enterprise_admin':
      return level !== 'global';
    case 'global_admin':
      return true;
    default:
      return false;
  }
}

/** Check if user role can set fail_silent mode */
export function canSetFailSilent(role: PolicyRole): boolean {
  return role === 'global_admin';
}

